import { useEffect, useState } from 'react';
import { useAISummaryStreaming } from '../../../../common/streaming/use-ai-summary-streaming';
import {
  ChatbotReference,
  MessageReferenceEvent,
  MessageUpdateEvent,
  StreamEndEvent,
  StreamEvent,
} from '../../../../models/chatbot/types';
import { transformSerializedCard } from '../../../../lib/chatbot-utils';
import {
  trackAISummarySuccess,
  trackAISummaryError,
} from '../../../../models/assistant/analytics/analytics';

interface AISummaryCacheEntry {
  content: string;
  threadId: string | undefined;
  isComplete: boolean;
  references: ChatbotReference[];
  copy?: { html: string; text: string };
}

const MAX_CACHE_SIZE = 10;

export const aiSummaryCache = new Map<string, AISummaryCacheEntry>();

// Listener registry: allows remounted components to "re-join" an in-progress stream.
type StreamListener = (entry: AISummaryCacheEntry) => void;
export const streamListeners = new Map<string, Set<StreamListener>>();

function notifyListeners(query: string, entry: AISummaryCacheEntry) {
  streamListeners.get(query)?.forEach((cb) => cb(entry));
}

function addStreamListener(query: string, cb: StreamListener) {
  if (!streamListeners.has(query)) {
    streamListeners.set(query, new Set());
  }
  streamListeners.get(query)!.add(cb);
}

function removeStreamListener(query: string, cb: StreamListener) {
  const listeners = streamListeners.get(query);
  if (listeners) {
    listeners.delete(cb);
    if (listeners.size === 0) streamListeners.delete(query);
  }
}

function evictOldestIfNeeded() {
  if (aiSummaryCache.size > MAX_CACHE_SIZE) {
    // Map iterates in insertion order, so the first key is the oldest entry
    const oldestKey = aiSummaryCache.keys().next().value;
    if (oldestKey !== undefined) {
      aiSummaryCache.delete(oldestKey);
    }
  }
}

export function useAISummaryCache(
  query: string,
  programId: string
): {
  content: string;
  isLoading: boolean;
  threadId: string | undefined;
  references: ChatbotReference[];
  copy: { html: string; text: string } | undefined;
} {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [references, setReferences] = useState<ChatbotReference[]>([]);
  const [copy, setCopy] = useState<{ html: string; text: string } | undefined>(
    undefined
  );

  const performSearch = useAISummaryStreaming(programId);

  useEffect(() => {
    if (!query || !programId) return;

    const cached = aiSummaryCache.get(query);

    // ── Branch 1: Complete cache hit → instant restore ──
    if (cached?.isComplete) {
      setContent(cached.content);
      setThreadId(cached.threadId);
      setReferences(cached.references);
      setCopy(cached.copy);
      setIsLoading(false);
      return;
    }

    // ── Branch 2: Stream in progress → re-join ──
    if (cached && !cached.isComplete) {
      setContent(cached.content);
      setThreadId(cached.threadId);
      setReferences(cached.references);
      setIsLoading(true);

      const listener: StreamListener = (entry) => {
        setContent(entry.content);
        setThreadId(entry.threadId);
        setReferences(entry.references);
        if (entry.isComplete) {
          setIsLoading(false);
          setCopy(entry.copy);
        }
      };
      addStreamListener(query, listener);

      return () => {
        removeStreamListener(query, listener);
      };
    }

    // ── Branch 3: No cache entry → start fresh stream ──
    let isMounted = true;

    setContent('');
    setIsLoading(true);
    setThreadId(undefined);
    setReferences([]);
    setCopy(undefined);

    aiSummaryCache.set(query, {
      content: '',
      threadId: undefined,
      isComplete: false,
      references: [],
    });
    evictOldestIfNeeded();

    performSearch(query, (event: StreamEvent) => {
      const entry = aiSummaryCache.get(query);

      switch (event.type) {
        case 'stream_start':
          if (entry) entry.threadId = event.data.threadId;
          if (isMounted) setThreadId(event.data.threadId);
          if (entry) notifyListeners(query, entry);
          break;

        case 'message_update': {
          const chunk = (event as MessageUpdateEvent).data.chunk;
          if (entry) entry.content += chunk;
          if (isMounted) setContent((prev: string) => prev + chunk);
          if (entry) notifyListeners(query, entry);
          break;
        }

        case 'message_reference': {
          const refEvent = event as MessageReferenceEvent;
          const card = transformSerializedCard(refEvent.data.card);
          const newRef: ChatbotReference = {
            referenceType: refEvent.data.type,
            referenceId: refEvent.data.id,
            card,
          };
          if (entry) {
            entry.references = [...entry.references, newRef];
          }
          if (isMounted) {
            setReferences((prev: ChatbotReference[]) => [...prev, newRef]);
          }
          if (entry) notifyListeners(query, entry);
          break;
        }

        case 'stream_end': {
          const endEvent = event as StreamEndEvent;
          if (entry) {
            entry.isComplete = true;
            entry.copy = endEvent.data?.assistantMessage?.copy;
          }
          if (isMounted) {
            setIsLoading(false);
            setCopy(endEvent.data?.assistantMessage?.copy);
          }
          if (entry) notifyListeners(query, entry);
          streamListeners.delete(query);
          trackAISummarySuccess({
            query,
            thread_id: entry?.threadId,
            reference_count: entry?.references.length || 0,
            has_content: !!entry?.content,
          });
          break;
        }

        case 'error': {
          const lastContent = entry?.content || '';
          const lastThreadId = entry?.threadId;
          const lastReferences = entry?.references || [];
          aiSummaryCache.delete(query);
          if (isMounted) setIsLoading(false);
          streamListeners.get(query)?.forEach((cb) =>
            cb({
              content: lastContent,
              threadId: lastThreadId,
              isComplete: true,
              references: lastReferences,
            })
          );
          streamListeners.delete(query);
          trackAISummaryError({
            query,
            thread_id: lastThreadId,
            error: event.data.error,
          });
          break;
        }

        default:
          break;
      }
    });

    return () => {
      isMounted = false;
    };
  }, [query, programId, performSearch]);

  return { content, isLoading, threadId, references, copy };
}
